// src/pages/DispatchStatus/DispatchStatus.jsx
import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  FiChevronDown,
  FiChevronUp,
  FiPackage,
  FiFilter,
  FiAlertTriangle,
  FiPrinter,
  FiSend,
  FiCheckCircle,
  FiXCircle,
  FiCalendar,
} from "react-icons/fi";
import request from "../../utils/request";
import SpinnerQ from "../../components/SpinnerQ/SpinnerQ";
import TablePaginationQ from "../../components/TableBionovaQ/TablePaginationQ/TablePaginationQ";
import RemitoDispatch from "../../components/RemitoDispatch/RemitoDispatch";
import df from "../../utils/dateFormater";
import parseFiltersDate from "../../utils/parseFiltersDate";
import { getRegionalFormat, getSession } from "../../utils/sessionHandler";
import PageContainer from "../../components/PageContainer/PageContainer";
import styles from "./DispatchStatus.module.css";
import t from "../../utils/translation";
import useTableQ from "../../utils/Hooks/useTableQ";
import Swal from "sweetalert2";
import { MdDoneAll, MdHourglassTop } from "react-icons/md";

const useDebouncedValue = (v, d = 400) => {
  const [deb, setDeb] = useState(v);
  useEffect(() => {
    const id = setTimeout(() => setDeb(v), d);
    return () => clearTimeout(id);
  }, [v, d]);
  return deb;
};

const chevronV = { closed: { rotate: 0 }, open: { rotate: 180 } };
const rowHoverV = {
  hover: { backgroundColor: "var(--orange-light)" },
  tap: { scale: 0.98 },
};

export default function DispatchStatus() {
  const [dispatches, setDispatches] = useState([]);
  const [areas, setAreas] = useState([]);
  const [operators, setOperators] = useState([]);
  const [openRows, setOpenRows] = useState({});
  const [openPkgs, setOpenPkgs] = useState({});
  const [loading, setLoading] = useState(false);
  const [selectedDispatchToPrint, setSelectedDispatchToPrint] = useState(null);
  const [totalPages, setTotalPages] = useState(1);

  const [filters, setFilters] = useState({
    dispatchNumber: "",
    packageNumber: "",
    dateFrom: "",
    dateTo: "",
    status: "",
    isUrgent: "",
    areaId: "",
    userName: "",
    receiverUser: "",
  });
  const F = useDebouncedValue(filters);

  const {
    currentPage,
    currentItems: currentDispatches,
    itemsPerPage,
    handleChangePage,
    handleChangeItemsPerPage,
    setCurrentPage,
  } = useTableQ(
    dispatches,
    [
      "dispatchNumber",
      "senderUser",
      "shippingResponsible",
      "receiverUser",
      "creationDate",
    ],
    totalPages
  );

  useEffect(() => {
    (async () => {
      try {
        const [aRes, opRes] = await Promise.all([
          request().get(`/api/area?companyId=${getSession().Company.Id}`),
          request().get(
            `/api/appuser/getByCompany?companyId=${getSession().Company.Id}`
          ),
        ]);
        setAreas(aRes.data || []);
        setOperators(opRes.data || []);
      } catch (err) {
        console.error("Error fetching areas/operators:", err);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const p = new URLSearchParams();
        p.append("page", currentPage);
        p.append("pageSize", itemsPerPage);

        if (F.dispatchNumber) p.append("dispatchNumber", F.dispatchNumber);
        if (F.packageNumber) p.append("packageNumber", F.packageNumber);
        if (F.status) p.append("dispatchStatus", F.status);
        if (F.isUrgent) p.append("isUrgent", F.isUrgent);
        if (F.areaId) p.append("areaId", F.areaId);
        if (F.userName) p.append("senderId", F.userName);
        if (F.receiverUser) p.append("receiverId", F.receiverUser);

        if (F.dateFrom)
          p.append(
            "dateFrom",
            parseFiltersDate(
              df(F.dateFrom, "date"),
              getRegionalFormat().dateFormat
            )
          );
        if (F.dateTo)
          p.append(
            "dateTo",
            parseFiltersDate(
              df(F.dateTo, "date"),
              getRegionalFormat().dateFormat,
              true
            )
          );

        const { data } = await request().get(`/api/dispatch/list?${p}`);
        setDispatches(data.dispatches || data);
        setTotalPages(data.totalPages);
      } catch (err) {
        console.error("Error fetching dispatch list:", err);
        setDispatches([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [F, currentPage, itemsPerPage]);

  const toggleRow = (id) => setOpenRows((p) => ({ ...p, [id]: !p[id] }));
  const togglePkg = (id) => setOpenPkgs((p) => ({ ...p, [id]: !p[id] }));
  const onFilter = (e) => {
    if (!e?.target) return;

    const name = e.target.getAttribute("name");
    const value = e.target.value;

    if (!name) return;

    setFilters((prevFilters) => ({
      ...prevFilters,
      [name]: value,
    }));
  };

  const renderDispatchStatus = (st) => {
    switch (st) {
      case 1: // DISPATCHED
        return (
          <span className={`${styles.badge} ${styles.badgeOrange}`}>
            <FiSend size={11} /> {t("BlankEntry196")}
          </span>
        );
      case 2: // ACCEPTED
        return (
          <span className={`${styles.badge} ${styles.green}`}>
            <FiCheckCircle size={12} /> {t("BlankEntry232")}
          </span>
        );
      case 3:
        return (
          <span className={`${styles.badge} ${styles.orange}`}>
            <FiXCircle size={12} /> {t("BlankEntry249")}
          </span>
        );
      case 5:
        return (
          <span className={`${styles.badge} ${styles.darkGreen}`}>
            <MdDoneAll size={12} /> {t("BlankEntry241")}
          </span>
        );

      case 4:
        return (
          <span className={`${styles.badge} ${styles.yellow}`}>
            <MdHourglassTop size={12} /> {t("BlankEntry242")}
          </span>
        );
      default:
        return null;
    }
  };

  const renderPackageStatus = (pkg) => {
    if (pkg.packageStatus === 10)
      return (
        <span className={`${styles.badge} ${styles.badgeOrange}`}>
          <FiSend size={11} /> {t("BlankEntry196")}
        </span>
      );
    if (pkg.packageStatus === 8)
      return (
        <span className={`${styles.badge} ${styles.badgeGreen}`}>
          <FiCheckCircle size={11} /> {t("BlankEntry198")}
        </span>
      );
    if (pkg.packageStatus === 9)
      return (
        <span className={`${styles.badge} ${styles.badgeRed}`}>
          <FiXCircle size={11} /> {t("BlankEntry199")}
        </span>
      );
    if (pkg.packageStatus === 11)
      return (
        <span className={`${styles.badge} ${styles.badgeOrange}`}>
          <FiAlertTriangle size={11} /> {t("BlankEntry200")}
        </span>
      );
    if (pkg.packageStatus === 12)
      return (
        <span className={`${styles.badge} ${styles.darkGreen}`}>
          <MdDoneAll size={12} /> {t("BlankEntry241")}
        </span>
      );
    if (pkg.packageStatus === 13)
      return (
        <span className={`${styles.badge} ${styles.yellow}`}>
          <MdHourglassTop size={12} /> {t("BlankEntry242")}
        </span>
      );
    return null;
  };

  const onPrint = (dispatchData) => {
    Swal.fire({
      title: t("BlankEntry191"),
      text: t("BlankEntry192"),
      icon: "question",
      input: "number",
      inputLabel: t("BlankEntry193"),
      inputValue: 1,
      inputAttributes: {
        min: 1,
        step: 1,
      },
      showCancelButton: true,
      confirmButtonText: t("BlankEntry194"),
      cancelButtonText: t("CancelButton"),
    }).then(async (result) => {
      if (result.isConfirmed) {
        const copies = parseInt(result.value, 10) || 1;
        setSelectedDispatchToPrint({
          ...dispatchData,
          copiesCount: copies,
        });
      }
    });
  };
  return (
    <PageContainer
      categoryId={4}
      category={t("BlankEntry166") || "Dispatch"}
      backUrl="/appdispatchmenu"
      title={t("BlankEntry168") || "Dispatch Status"}
    >
      <div className={styles.screenWrapper}>
        {/* Filtros */}
        <section className={styles.filtersBar}>
          <FiFilter className={styles.filterIcon} />
          <input
            name="dispatchNumber"
            placeholder={t("BlankEntry201")}
            value={filters.dispatchNumber}
            onChange={onFilter}
          />
          {/* <input
            name="packageNumber"
            placeholder="Package #"
            value={filters.packageNumber}
            onChange={onFilter}
          /> */}
          <select name="status" value={filters.status} onChange={onFilter}>
            <option value="">{t("BlankEntry202")}</option>
            <option value="1">{t("BlankEntry196")}</option>
            <option value="2">{t("BlankEntry197")}</option>
          </select>
          <select name="isUrgent" value={filters.isUrgent} onChange={onFilter}>
            <option value="">{t("BlankEntry175")}</option>
            <option value="true">{t("BlankEntry176")}</option>
            <option value="false">{t("BlankEntry177")}</option>
          </select>
          <select name="areaId" value={filters.areaId} onChange={onFilter}>
            <option value="">{t("BlankEntry203")}</option>
            {areas.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
          <select name="userName" value={filters.userName} onChange={onFilter}>
            <option value="">{t("BlankEntry204")}</option>
            {operators.map((op) => (
              <option key={op.id} value={op.id}>
                {op.toString}
              </option>
            ))}
          </select>
          <select
            name="receiverUser"
            value={filters.receiverUser}
            onChange={onFilter}
          >
            <option value="">{t("BlankEntry205")}</option>
            {operators.map((op) => (
              <option key={op.id} value={op.id}>
                {op.toString}
              </option>
            ))}
          </select>
        </section>

        {/* Tabla */}
        <div className={styles.tableWrapper}>
          <div className={styles.tableHeader}>
            <span />
            <span>{t("BlankEntry201")}</span>
            <span>{t("BlankEntry206")}</span>
            <span>{t("BlankEntry173")}</span>
            <span>{t("BlankEntry206")}</span>
            <span>{t("DateSpan")}</span>
            <span>{t("StatusSpan")}</span>
            <span>{t("BlankEntry207")}</span>
            <span>{t("BlankEntry174")}</span>
            <span>{t("PrintLabelSpan")}</span>
          </div>

          {(loading || currentDispatches.length === 0) && (
            <div className="spinnerContainer">
              {loading ? (
                <SpinnerQ category="dispatch" />
              ) : (
                <p>{t("NoData") || "No data loaded."}</p>
              )}
            </div>
          )}

          {!loading && currentDispatches.length > 0 && (
            <AnimatePresence initial={false}>
              {currentDispatches.map((d) => {
                const open = !!openRows[d.dispatchNumber];
                return (
                  <motion.div
                    key={d.dispatchNumber}
                    layout
                    className={styles.rowBundle}
                  >
                    <motion.button
                      className={styles.row}
                      onClick={() => toggleRow(d.dispatchNumber)}
                      variants={rowHoverV}
                      whileHover="hover"
                      whileTap="tap"
                    >
                      <motion.span
                        variants={chevronV}
                        animate={open ? "open" : "closed"}
                        className={styles.chevron}
                      >
                        {open ? <FiChevronUp /> : <FiChevronDown />}
                      </motion.span>

                      <span className={styles.dispatchNumber}>
                        {d.dispatchNumber}
                      </span>
                      <span>{d.senderUser}</span>
                      <span>{d.shippingResponsible}</span>
                      <span>{d.receiverUser || "—"}</span>
                      <span>{df(d.creationDate, "dateTime")}</span>
                      <span>{renderDispatchStatus(d.dispatchStatus)}</span>
                      <span>{d.areaName}</span>
                      <span>
                        {d.isUrgent ? (
                          <span
                            className={`${styles.badge} ${styles.badgeOrange}`}
                          >
                            <FiAlertTriangle size={12} /> {t("BlankEntry176")}
                          </span>
                        ) : (
                          <span
                            className={`${styles.badge} ${styles.badgeScheduled}`}
                          >
                            <FiCalendar size={12} /> {t("BlankEntry175")}
                          </span>
                        )}
                      </span>
                      <span
                        className={styles.printButton}
                        onClick={(e) => {
                          e.stopPropagation();

                          onPrint(d);
                        }}
                      >
                        <FiPrinter />
                      </span>
                    </motion.button>

                    {!open && <div className={styles.spacer} />}

                    {/* Paquetes */}
                    <AnimatePresence>
                      {open && (
                        <motion.div layout className={styles.packagesWrapper}>
                          {d.packages.map((pkg) => {
                            const pOpen = !!openPkgs[pkg.packageId];
                            return (
                              <motion.div key={pkg.packageId} layout>
                                <button
                                  className={styles.packageRow}
                                  onClick={() => togglePkg(pkg.packageId)}
                                >
                                  <motion.span
                                    variants={chevronV}
                                    animate={pOpen ? "open" : "closed"}
                                    className={styles.chevronPkg}
                                  >
                                    {pOpen ? (
                                      <FiChevronUp />
                                    ) : (
                                      <FiChevronDown />
                                    )}
                                  </motion.span>
                                  <FiPackage className={styles.pkgIcon} />
                                  <span className={styles.pkgNumber}>
                                    {pkg.packageNumber}
                                  </span>
                                  {renderPackageStatus(pkg)}
                                  <span className={styles.pkgName}>
                                    {pkg.packageName}
                                  </span>
                                  <span className={styles.materialCount}>
                                    {pkg.materials.length} {t("MaterialsSpan")}
                                  </span>
                                </button>

                                <AnimatePresence>
                                  {pOpen && (
                                    <motion.table
                                      layout
                                      className={styles.materialTable}
                                    >
                                      <thead>
                                        <tr>
                                          <th>{t("MaterialSpan")}</th>
                                          <th>{t("SerialSpan")}</th>

                                          <th>{t("QuantitySpan")}</th>
                                          <th>{t("Implant")}</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {pkg.materials.map((m) => (
                                          <tr
                                            key={`${m.materialId}-${
                                              m.serial || "_"
                                            }`}
                                          >
                                            <td>{m.materialName}</td>
                                            <td>{m.serial || "—"}</td>
                                            <td>{m.quantity}</td>
                                            <td>
                                              {m.isImplant ? (
                                                <FiCheckCircle />
                                              ) : (
                                                "—"
                                              )}
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </motion.table>
                                  )}
                                </AnimatePresence>
                              </motion.div>
                            );
                          })}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          )}

          <TablePaginationQ
            currentPage={currentPage}
            totalPages={totalPages}
            handleChangePage={handleChangePage}
            handleChangeItemsPerPage={handleChangeItemsPerPage}
            setCurrentPage={setCurrentPage}
          />
        </div>
      </div>

      {selectedDispatchToPrint && (
        <div style={{ position: "absolute", left: "-9999px", top: "-9999px" }}>
          <RemitoDispatch dispatchData={selectedDispatchToPrint} />
        </div>
      )}
    </PageContainer>
  );
}
